import { ReactNode, forwardRef } from 'react';
import cn from 'classnames';

import linkStyles from '../SimpleLink/SimpleLink.module.scss';
import styles from './SimpleButton.module.scss';

export interface SimpleButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'ref'> {
  children: ReactNode;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  mode?: 'light' | 'dark';
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * SimpleButton: same look as SimpleLink, rendered as a native button.
 * @param variant - 'primary' for no underline, 'secondary' for underlined
 * @param size - Only applies to 'secondary' variant: 'small' or 'medium'
 * @param mode - 'light' for dark text on light backgrounds (default), 'dark' for light text on dark backgrounds
 * @default variant 'primary'
 * @default mode 'light'
 * @default type 'button'
 */
const SimpleButton = forwardRef<HTMLButtonElement, SimpleButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size,
      mode = 'light',
      className,
      type = 'button',
      onClick,
      ...rest
    },
    ref
  ) => {
    const SIZE_STYLES = {
      small: linkStyles.sizeSmall,
      medium: linkStyles.sizeMedium,
    } as const;

    const VARIANT_STYLES = {
      primary: linkStyles.primary,
      secondary: linkStyles.secondary,
    } as const;

    const MODE_STYLES = {
      light: linkStyles.light,
      dark: linkStyles.dark,
    } as const;

    const sizeClass =
      variant === 'secondary' && size ? SIZE_STYLES[size] : null;
    const modeClass = MODE_STYLES[mode];

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          styles.root,
          VARIANT_STYLES[variant],
          modeClass,
          sizeClass,
          className
        )}
        onClick={onClick ?? undefined}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

SimpleButton.displayName = 'SimpleButton';

export default SimpleButton;
